import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { formatFecha, diasRestantes } from '../data/fechas2026'
import styles from '../CalendarioTributario.module.css'

const API_KEY = import.meta.env.VITE_ANTHROPIC_API_KEY

const AI_SYSTEM = `Eres el Asistente Tributario y Contable de Ingconia — Soluciones Inteligentes, una firma colombiana especializada en contabilidad y asesoría tributaria.

Tu especialidad es la normativa tributaria y contable colombiana vigente para 2026. Conoces en detalle:
- DIAN: IVA, Retención en la Fuente, Renta Personas Jurídicas y Naturales, Exógena, RUB, GMF
- Secretaría de Hacienda Bogotá: ICA, ReteICA, Exógena Distrital
- UGPP / Mi Planilla: aportes a seguridad social, PILA, parafiscales
- Normas clave: Estatuto Tributario, Decreto 2229/2023, Decreto 780/2016, Res.000227/2025 DIAN
- Contabilidad: NIIF, PCG, estados financieros, conciliaciones
- Régimen Simple de Tributación, Régimen Ordinario, Grandes Contribuyentes
- Sanciones, intereses moratorios, correcciones, recursos tributarios

Responde siempre:
- En español colombiano, tono profesional pero cercano
- Conciso y preciso — máximo 3-4 párrafos o usa listas cortas
- Con norma específica cuando sea relevante (ej: "Art. 641 E.T.")
- Nunca inventes fechas o normas — si no estás seguro, dilo
- Si la pregunta no es tributaria/contable, redirige amablemente a tu especialidad
- Termina ofreciendo más ayuda o sugiriendo tema relacionado`

function mdToHtml(t) {
    return t
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
        .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
        .replace(/\*(.+?)\*/g,'<em>$1</em>')
        .replace(/`(.+?)`/g,'<code style="background:#f0f4f8;padding:1px 4px;border-radius:3px;font-size:11px">$1</code>')
        .replace(/^- (.+)/gm,'<li>$1</li>')
        .replace(/(<li>.*<\/li>)/s,'<ul style="margin:6px 0 6px 16px">$1</ul>')
        .replace(/\n\n/g,'<br/><br/>').replace(/\n/g,'<br/>')
}

const SUGERENCIAS = [
    '¿Cuándo vence mi IVA?',
    '¿Qué es el RUB?',
    'Sanción por no declarar',
    '¿Qué es retención en la fuente?',
    'Diferencia IVA bimestral vs cuatrimestral',
    '¿Cómo se calcula la PILA?',
]

export default function AsistenteIA({ datos, nit }) {
    const { hasModule } = useAuth()

    // El bloque se muestra solo si:
    // 1. La empresa tiene el módulo 'asistente_ia' habilitado
    // 2. La API key de Anthropic está configurada
    const habilitado = hasModule('asistente_ia') && !!API_KEY

    const [open, setOpen]       = useState(false)
    const [msgs, setMsgs]       = useState([])
    const [input, setInput]     = useState('')
    const [loading, setLoading] = useState(false)
    const [iniciado, setIniciado] = useState(false)
    const [notif, setNotif]     = useState(false)
    const history               = useRef([])
    const msgsRef               = useRef(null)

    useEffect(() => {
        if (!habilitado) return
        const t = setTimeout(() => { if (!open) setNotif(true) }, 4000)
        return () => clearTimeout(t)
    }, [open, habilitado])

    useEffect(() => {
        if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight
    }, [msgs, loading])

    // No renderizar nada si el módulo no está habilitado
    if (!habilitado) return null

    function toggleAI() {
        setOpen(o => !o)
        setNotif(false)
        if (!iniciado) {
            setIniciado(true)
            addBot('¡Hola! 👋 Soy el asistente tributario de **Ingconia**.\n\nEstoy listo para resolver tus dudas sobre tributario, contabilidad, DIAN, PILA, ICA Bogotá y más.\n\n¿En qué te puedo ayudar hoy?')
        }
    }

    function addBot(md) {
        const t = new Date().toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})
        setMsgs(m => [...m, { role: 'bot', md, t }])
    }

    async function enviar(texto) {
        const txt = texto || input.trim()
        if (!txt || loading) return
        setInput('')
        const t = new Date().toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})
        setMsgs(m => [...m, { role: 'user', text: txt, t }])
        history.current.push({ role: 'user', content: txt })
        setLoading(true)

        // Contexto del NIT actual
        let ctx = ''
        if (datos && datos.length > 0) {
            const venc  = datos.filter(v => { const d = diasRestantes(v.f); return d !== null && d <= 15 && d > 0 })
            const venc2 = datos.filter(v => { const d = diasRestantes(v.f); return d !== null && d <= 0 })
            ctx = `\n\nCONTEXTO SEMÁFORO:\nNIT: ${nit}\nTotal: ${datos.length}\nVencidas: ${venc2.length}\nPróximas ≤15d: ${venc.length}`
            if (venc.length > 0) ctx += `\nDetalle: ${venc.slice(0,3).map(v=>v.ob+' ('+formatFecha(v.f)+')').join(', ')}`
        }

        try {
            const res = await fetch('https://api.anthropic.com/v1/messages', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'anthropic-version': '2023-06-01',
                    'anthropic-dangerous-direct-browser-access': 'true',
                },
                body: JSON.stringify({
                    model: 'claude-opus-4-5',
                    max_tokens: 1000,
                    system: AI_SYSTEM + ctx,
                    messages: history.current.slice(-10)
                })
            })
            const data = await res.json()
            if (data.content && data.content[0]) {
                const respuesta = data.content.map(c => c.text || '').join('')
                history.current.push({ role: 'assistant', content: respuesta })
                addBot(respuesta)
            } else if (data.error) {
                addBot('⚠️ **Error:** ' + (data.error.message || 'Intenta de nuevo.'))
            }
        } catch {
            addBot('⚠️ Sin conexión con el asistente. Verifica tu internet e intenta de nuevo.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {/* Botón flotante */}
            <button className={styles.aiBtn} onClick={toggleAI} title="Asistente IA Tributario">
                🤖
                {notif && <span className={styles.aiNotif}>1</span>}
            </button>

            {/* Panel */}
            {open && (
                <div className={styles.aiPanel}>
                    <div className={styles.aiHeader}>
                        <div className={styles.aiAvatar}>🤖</div>
                        <div className={styles.aiHinfo}>
                            <div className={styles.aiHname}>Asistente Tributario · Ingconia</div>
                            <div className={styles.aiHsub}>Experto en tributario y contable Colombia 2026</div>
                        </div>
                        <div className={styles.aiOnline}></div>
                        <button className={styles.aiClose} onClick={toggleAI}>✕</button>
                    </div>

                    <div className={styles.aiMsgs} ref={msgsRef}>
                        {msgs.map((m,i) => (
                            <div key={i} className={`${styles.msg} ${m.role === 'user' ? styles.msgUser : styles.msgBot}`}>
                                <div
                                    className={styles.msgBubble}
                                    {...(m.role === 'bot'
                                        ? { dangerouslySetInnerHTML: { __html: mdToHtml(m.md) } }
                                        : { children: m.text }
                                    )}
                                />
                                <span className={styles.msgTime}>
                                    {m.role === 'bot' ? `Ingconia IA · ${m.t}` : m.t}
                                </span>
                            </div>
                        ))}
                        {loading && (
                            <div className={`${styles.msg} ${styles.msgBot}`}>
                                <div className={styles.typingBubble}>
                                    <div className={styles.typingDot}></div>
                                    <div className={styles.typingDot}></div>
                                    <div className={styles.typingDot}></div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sugerencias rápidas */}
                    {msgs.length <= 1 && (
                        <div className={styles.aiSugs}>
                            {SUGERENCIAS.map((s,i) => (
                                <span key={i} className={styles.sugChip} onClick={() => enviar(s)}>{s}</span>
                            ))}
                        </div>
                    )}

                    <div className={styles.aiInputWrap}>
                        <textarea
                            className={styles.aiInput}
                            placeholder="Pregunta sobre tributario, contabilidad, DIAN..."
                            rows={1}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar() } }}
                        />
                        <button className={styles.aiSend} onClick={() => enviar()} disabled={loading}>➤</button>
                    </div>
                    <div className={styles.aiFooter}>⚡ Powered by Claude AI · Ingconia Soluciones Inteligentes</div>
                </div>
            )}
        </>
    )
}
